import { z } from "zod";

import { CLINIC_FAQ_SCOPE, PATIENT_RECORDS_SCOPE } from "@/shared/agent-prompts/prompts";

export enum QaIntent {
  AUTHORIZATION_OR_REFERRAL_QUESTION = "authorization_or_referral_question",
  BILLING_OR_PRICING = "billing_or_pricing",
  CANCEL = "cancel",
  CLINICAL_QUESTION = "clinical_question",
  CONFIRM = "confirm",
  EXTENSION_DIAL = "extension_dial",
  FORMS_OR_MEDICAL_RECORDS = "forms_or_medical_records",
  GENERAL_CLINIC_QUESTION = "general_clinic_question",
  INSURANCE_QUESTION = "insurance_question",
  LANGUAGE_SUPPORT = "language_support",
  MESSAGE_FOR_STAFF = "message_for_staff",
  NON_RECORDED_LINE = "non_recorded_line",
  OTHER = "other",
  PRESCRIPTION_REFILL = "prescription_refill",
  PROVIDER_TRANSFER = "provider_transfer",
  REQUESTED_HUMAN = "requested_human",
  RESCHEDULE = "reschedule",
  SCHEDULE = "schedule",
  SPAM = "spam",
  TELEHEALTH_OR_CONNECTION_HELP = "telehealth_or_connection_help",
  UNCONFIRMED = "unconfirmed",
}

export enum QaIntentDisposition {
  COMPLETED = "completed",
  NOT_COMPLETED = "not_completed",
}

export const voiceIntentSchema = z.enum(QaIntent).describe(`
A distinct caller need expressed anywhere in the call, including an agreed alternative that was actually requested.
- schedule: book a new appointment, for either a new or established patient.
- reschedule: move an existing appointment; cancel: cancel an appointment; confirm: confirm an upcoming appointment.
- authorization_or_referral_question: referral or prior authorization status.
- billing_or_pricing: billing, charges, payment, or pricing; insurance_question: insurance coverage or network questions.
- clinical_question: symptoms or clinical advice; prescription_refill: refill or renew a prescription.
- general_clinic_question: ${CLINIC_FAQ_SCOPE}.
- forms_or_medical_records: ${PATIENT_RECORDS_SCOPE}.
- message_for_staff: leave a message for a person or department, including an agreed staff-message alternative.
- telehealth_or_connection_help: help joining a telehealth visit or technical support.
- language_support: interpreter or other-language assistance.
- non_recorded_line: request the non-recorded line; extension_dial: request a known extension. Recorded keypad selections count.
- provider_transfer: a clinician calling to reach staff; requested_human: an explicit request to speak to a human.
- spam: unsolicited marketing, scams, or robocalls unrelated to patient care.
- other: a clearly stated need outside these categories; unconfirmed: no caller need can be determined.
Do not invent an additional intent for routine workflow steps or an offered alternative the caller did not accept.
`);

export const intentDispositionSchema = z.enum(QaIntentDisposition).describe(`
Whether this individual caller need was fulfilled during the call, independently of overall callOutcome.
completed requires evidence in the conversation or successful tool results, not a promise, offer, or failed attempt.
An unresolved scheduling, billing, or clinical request remains not_completed after a handoff or staff message.
A successful connection completes an explicit requested_human, provider_transfer, extension_dial, or non_recorded_line request.
A successfully submitted staff message completes message_for_staff; it does not complete the original unresolved request.
When several asks share an intent category, mark completed only if all those asks were fulfilled; otherwise not_completed.
Use not_completed for unconfirmed. For spam, completed means the solicitation was appropriately declined or ended.
`);

export const voiceIntentResultSchema = z.object({
  intent: voiceIntentSchema,
  disposition: intentDispositionSchema,
});

export const voiceIntentResultsSchema = z
  .array(voiceIntentResultSchema)
  .min(1)
  .refine((results) => new Set(results.map(({ intent }) => intent)).size === results.length, {
    message: "Each intent category must appear exactly once",
  })
  .refine((results) => results.length === 1 || results.every(({ intent }) => intent !== QaIntent.UNCONFIRMED), {
    message: "Unconfirmed cannot be combined with identified intents",
  })
  .refine(
    (results) =>
      results.every(
        ({ intent, disposition }) =>
          intent !== QaIntent.UNCONFIRMED || disposition === QaIntentDisposition.NOT_COMPLETED
      ),
    { message: "An unconfirmed intent cannot be completed" }
  )
  .describe(
    "All distinct caller needs across the full call, one entry per intent category, each assessed independently. Include completed and unresolved needs. Return only unconfirmed/not_completed when no need can be determined."
  );

export type VoiceIntentResult = z.infer<typeof voiceIntentResultSchema>;
